import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";

const API_URL = "https://comoticket-production.up.railway.app/api";
const EUR_TO_KMF = 491.96;
const toKMF = (eur) => Math.round(parseFloat(eur) * EUR_TO_KMF).toLocaleString("fr-FR");

const getImageBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = url;
  });
};

export default function MyTicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("comoticket_customer_token");
    const savedCustomer = localStorage.getItem("comoticket_customer");
    if (!token) {
      navigate("/compte/connexion");
      return;
    }
    setCustomer(savedCustomer ? JSON.parse(savedCustomer) : null);

    fetch(API_URL + "/customer/my-tickets", {
      headers: { Authorization: "Bearer " + token },
    })
      .then(r => {
        if (r.status === 401) { handleLogout(); return []; }
        return r.json();
      })
      .then(data => { setTickets(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("comoticket_customer_token");
    localStorage.removeItem("comoticket_customer");
    navigate("/compte/connexion");
  };

  const downloadPDF = async (t) => {
    setDownloadingId(t.id);
    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: [100, 180] });

      doc.setFillColor(10, 10, 10);
      doc.rect(0, 0, 100, 180, "F");

      doc.setFillColor(37, 99, 235);
      doc.rect(0, 0, 100, 28, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("ComoTicket", 50, 16, { align: "center" });
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text("Billet electronique", 50, 23, { align: "center" });

      let y = 38;
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      const eventNameLines = doc.splitTextToSize(t.event_name, 88);
      doc.text(eventNameLines, 50, y, { align: "center" });
      y += eventNameLines.length * 6 + 4;

      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(200, 200, 200);
      const dStart = new Date(t.event_date);
      const pad = n => String(n).padStart(2,"0");
      const startStr = pad(dStart.getDate()) + "/" + pad(dStart.getMonth()+1) + "/" + dStart.getFullYear() + " " + pad(dStart.getHours()) + "h" + pad(dStart.getMinutes());
      const endStr = t.event_end_date ? " -> " + pad(new Date(t.event_end_date).getHours()) + "h" + pad(new Date(t.event_end_date).getMinutes()) : "";
      const dateStr = startStr + endStr;
      doc.text(dateStr, 50, y, { align: "center" });
      y += 6;
      doc.text(t.event_location, 50, y, { align: "center" });
      y += 12;

      if (t.qr_url) {
        try {
          const imgData = await getImageBase64(t.qr_url);
          doc.addImage(imgData, "PNG", 25, y, 50, 50);
          y += 56;
        } catch (e) {
          y += 4;
        }
      }

      doc.setDrawColor(60, 60, 60);
      doc.setLineDashPattern([2, 2], 0);
      doc.line(8, y, 92, y);
      y += 8;

      doc.setFontSize(9);
      doc.setTextColor(200, 200, 200);
      doc.setFont("helvetica", "normal");

      const addRow = (label, value) => {
        doc.text(label, 10, y);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(255, 255, 255);
        doc.text(String(value), 90, y, { align: "right" });
        doc.setFont("helvetica", "normal");
        doc.setTextColor(200, 200, 200);
        y += 7;
      };

      const pdfKMF = Math.round(parseFloat(t.category_price) * EUR_TO_KMF).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");

      addRow("Titulaire", t.holder_name || "-");
      addRow("Categorie", t.category_name || "-");
      addRow("Prix", pdfKMF + " KMF");
      addRow("Reference", "#" + (t.sale_reference || t.id));
      addRow("Statut", t.status === "used" ? "Utilise" : "Valide");
      if (t.sold_at) {
        const soldDate = new Date(t.sold_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
        addRow("Achete le", soldDate);
      }
      if (t.merchant_shop_name || t.merchant_name) {
        addRow("Point de vente", t.merchant_shop_name || t.merchant_name);
      }

      y += 4;
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.text("Presentez ce billet (ecran ou imprime) a l entree.", 50, y, { align: "center" });
      y += 4;
      doc.text("Billet non remboursable - ComoTicket", 50, y, { align: "center" });

      doc.save("billet-" + (t.sale_reference || t.id) + ".pdf");
    } catch (e) {
      alert("Erreur lors de la generation du PDF");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div style={{minHeight:"100vh",background:"#0a0a0a",fontFamily:"'Inter',Arial,sans-serif",color:"white"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
      `}</style>

      <nav style={{position:"sticky",top:0,zIndex:100,background:"rgba(10,10,10,0.95)",backdropFilter:"blur(20px)",borderBottom:"1px solid rgba(255,255,255,0.06)",padding:"16px 40px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <a href="/events" style={{display:"flex",alignItems:"center",gap:10,textDecoration:"none"}}>
          <div style={{width:36,height:36,background:"#2563eb",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontWeight:800,color:"white"}}>C</div>
          <span style={{fontSize:18,fontWeight:800,letterSpacing:"-0.5px",color:"white"}}>ComoTicket</span>
        </a>
        <button onClick={handleLogout} style={{background:"rgba(255,255,255,0.08)",border:"1.5px solid rgba(255,255,255,0.2)",color:"white",padding:"9px 18px",borderRadius:100,fontWeight:700,fontSize:13,cursor:"pointer"}}>D&eacute;connexion</button>
      </nav>

      <div style={{maxWidth:800,margin:"0 auto",padding:"60px 40px"}}>
        <h1 style={{fontSize:32,fontWeight:900,letterSpacing:"-1px",marginBottom:8}}>Mes billets</h1>
        <p style={{fontSize:14,color:"rgba(255,255,255,0.5)",marginBottom:40}} dangerouslySetInnerHTML={{__html: customer ? "Bonjour " + customer.name + ", voici tous vos billets ComoTicket" : ""}} />

        {loading ? (
          <div style={{textAlign:"center",padding:60,color:"rgba(255,255,255,0.3)"}}>Chargement...</div>
        ) : tickets.length === 0 ? (
          <div style={{textAlign:"center",padding:60,background:"#111",borderRadius:16,border:"1px solid rgba(255,255,255,0.06)"}}>
            <div style={{fontSize:48,marginBottom:16}}>&#127915;</div>
            <div style={{fontSize:16,color:"rgba(255,255,255,0.5)",marginBottom:20}}>Aucun billet trouv&eacute; pour le moment</div>
            <p style={{fontSize:13,color:"rgba(255,255,255,0.3)",marginBottom:20}}>Vos billets appara&icirc;tront ici si vous avez utilis&eacute; cette m&ecirc;me adresse email lors de votre achat.</p>
            <a href="/events" style={{display:"inline-block",background:"#2563eb",color:"white",padding:"12px 28px",borderRadius:100,fontWeight:700,fontSize:14,textDecoration:"none"}}>Voir les &eacute;v&eacute;nements</a>
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            {tickets.map((t) => (
              <div key={t.id} style={{background:"#111",borderRadius:16,overflow:"hidden",border:"1px solid rgba(255,255,255,0.06)",display:"grid",gridTemplateColumns:"140px 1fr"}}>
                <div style={{background:"#1a1a2e",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
                  {t.event_image ? (
                    <img src={t.event_image} alt={t.event_name} style={{width:"100%",height:"100%",objectFit:"cover"}} />
                  ) : (
                    <div style={{fontSize:32}}>&#127915;</div>
                  )}
                </div>
                <div style={{padding:20}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                    <h3 style={{fontSize:17,fontWeight:800}}>{t.event_name}</h3>
                    <span style={{background:t.status==="used"?"rgba(239,68,68,0.15)":"rgba(16,185,129,0.15)",color:t.status==="used"?"#fca5a5":"#6ee7b7",padding:"4px 10px",borderRadius:100,fontSize:11,fontWeight:700}}>
                      <span dangerouslySetInnerHTML={{__html: t.status === "used" ? "Utilis&eacute;" : "Valide"}} />
                    </span>
                  </div>
                  <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",marginBottom:4}}>
                    &#128197; {new Date(t.event_date).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric",hour:"2-digit",minute:"2-digit",timeZone:"UTC"})}{t.event_end_date ? " → " + new Date(t.event_end_date).toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit",timeZone:"UTC"}) : ""}
                  </div>
                  <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",marginBottom:12}}>
                    &#128205; {t.event_location}
                  </div>
                  <div style={{display:"flex",gap:16,fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:14}}>
                    <span>Cat&eacute;gorie: <strong style={{color:"white"}}>{t.category_name}</strong></span>
                    <span>Prix: <strong style={{color:"white"}}>{toKMF(t.category_price)} KMF</strong></span>
                    <span>R&eacute;f: <strong style={{color:"white"}}>#{t.sale_reference || t.id}</strong></span>
                  </div>
                  <button onClick={() => downloadPDF(t)} disabled={downloadingId === t.id} style={{background:"#2563eb",border:"none",color:"white",padding:"8px 16px",borderRadius:8,fontWeight:700,fontSize:12,cursor:"pointer",opacity:downloadingId===t.id?0.6:1}} dangerouslySetInnerHTML={{__html: downloadingId === t.id ? "G&eacute;n&eacute;ration..." : "&#11015; T&eacute;l&eacute;charger mon billet"}} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}